// app/javascript/components/EditUser.jsx
import React, { useState, useEffect } from 'react';
import { t } from '../utils/i18n';

const EditUser = ({ onBack, onNavigate, api, userId }) => {
  const [formData, setFormData] = useState({
    email: '',
    army_id: '',
    location_id: '',
    department_id: ''
  });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
    fetchUser();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesData, locationsData, departmentsData] = await Promise.all([
        api.get('/users/available_roles'),
        api.get('/locations'),
        api.get('/departments')
      ]);
      setAvailableRoles(rolesData.roles);
      setLocations(locationsData);
      setDepartments(departmentsData);
    } catch (err) {
      setError('Failed to load form data');
    }
  };

  const fetchUser = async () => {
    try {
      const user = await api.get(`/users/${userId}`);
      setFormData({
        email: user.email || '',
        army_id: user.army_id || '',
        location_id: user.location_id || '',
        department_id: user.department_id || ''
      });
      setSelectedRoles(user.roles?.map(role => role.name) || []);
    } catch (err) {
      setError('Failed to load user data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { response, data } = await api.put(`/users/${userId}`, { 
        user: formData,
        role_names: selectedRoles
      });
      
      if (response.ok) {
        onNavigate('users');
      } else {
        setError(data.errors?.join(', ') || 'Failed to update user');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (roleName) => {
    setSelectedRoles(prev => 
      prev.includes(roleName) 
        ? prev.filter(r => r !== roleName)
        : [...prev, roleName]
    );
  };

  return (
    <div>
      <button onClick={() => onNavigate('users')}>← Back to Users</button>
      <h2>Edit User</h2>
      
      {error && <div style={{color: 'red'}}>{error}</div>}
      
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1rem' }}>
          <input
            name="email"
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <input
            name="army_id"
            placeholder="Army ID"
            value={formData.army_id}
            onChange={handleChange}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <select name="location_id" value={formData.location_id} onChange={handleChange}>
            <option value="">Select Location</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <select name="department_id" value={formData.department_id} onChange={handleChange}>
            <option value="">Select Department</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <h4>Assign Roles:</h4>
          {availableRoles.map(role => (
            <label key={role.id} style={{ display: 'block', margin: '5px 0' }}>
              <input
                type="checkbox"
                checked={selectedRoles.includes(role.name)}
                onChange={() => handleRoleChange(role.name)}
              />
              {role.name}
            </label>
          ))}
        </div>
        
        <button type="submit" disabled={loading}>
          {loading ? 'Updating...' : 'Update User'}
        </button>
        <button type="button" onClick={() => onNavigate('users')} style={{ marginLeft: '1rem' }}>
          {t('cancel')}
        </button>
      </form>
    </div>
  );
};

export default EditUser;
